import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import LoopIcon from "@mui/icons-material/Loop";
import { alpha } from "@mui/material/styles";

/**
 * Vertical step overview shown on the left of inside-exploration pages.
 *
 * Visual:
 *   - subtle gray panel
 *   - top: "EXPLORATION" eyebrow + iteration value (large)
 *   - below: vertical step list with current/done/upcoming states
 *   - thin connector line between step dots
 *
 * Props:
 *   steps:        [{ key, label, short? }, ...]
 *   currentIndex: number (0-based index into steps)
 *   iterationValue: number | string | null  (shown as the "exploration number")
 *   iterationLabel: string  (eyebrow above the value, default "EXPLORATION")
 *   populationLabel: string | null  (caption under the iteration number,
 *     e.g. "Input collection (P0)" / "Generated collection (Q0)" — disambiguates
 *     the two halves of iteration 0, which both share "Iteration #0")
 *   loop: { stepKeys, label, earlier } | null  (steps repeated per iteration,
 *     e.g. the image selection's width group -> primaries -> predictability:
 *     drawn on one tinted band with ``label`` ("Iteration 2") at its first
 *     step and the ``earlier`` iterations' one-line summaries under its last)
 */
export default function ExplorationStepSidebar({
    steps = [],
    currentIndex = 0,
    iterationValue = null,
    iterationLabel = "EXPLORATION",
    populationLabel = null,
    /** Caption next to the big value (selection flows pass e.g. "Samples"). */
    valueCaption = "Iteration",
    loop = null,
}) {
    if (!Array.isArray(steps) || steps.length === 0) return null;

    const totalSteps = steps.length;
    const currentStepNumber = Math.min(Math.max(currentIndex + 1, 1), totalSteps);
    const loopIndexes = loop
        ? steps.map((step, idx) => (loop.stepKeys.includes(step.key) ? idx : -1)).filter((idx) => idx >= 0)
        : [];
    const loopFirst = loopIndexes.length ? loopIndexes[0] : -1;
    const loopLast = loopIndexes.length ? loopIndexes[loopIndexes.length - 1] : -1;

    return (
        <Box
            component="aside"
            aria-label="Exploration progress"
            sx={{
                width: { xs: "100%", md: 268 },
                flexShrink: 0,
                alignSelf: "stretch",
                borderRadius: 3,
                border: "1px solid",
                // Sidebar carries the persistent, exploration-wide context, so
                // it sits in a clearly darker neutral gray than the per-page
                // gradient header in ``ExplorationPageShell``.
                borderColor: "rgba(15, 23, 42, 0.12)",
                bgcolor: "#dfe3ea",
                p: { xs: 2, md: 2.5 },
                boxSizing: "border-box",
                position: { md: "sticky" },
                top: { md: 16 },
            }}
        >
            <Stack spacing={0.5} sx={{ mb: 2.5 }}>
                <Typography
                    variant="overline"
                    sx={{
                        color: "text.secondary",
                        letterSpacing: 1.2,
                        fontWeight: 700,
                        lineHeight: 1.2,
                    }}
                >
                    {iterationLabel}
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                        variant="h4"
                        component="div"
                        sx={{
                            fontWeight: 800,
                            color: "text.primary",
                            lineHeight: 1.1,
                            letterSpacing: "-0.5px",
                        }}
                    >
                        {iterationValue !== null && iterationValue !== undefined && iterationValue !== ""
                            ? `#${iterationValue}`
                            : "#–"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                        {valueCaption}
                    </Typography>
                </Stack>
                {populationLabel ? (
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.primary",
                            fontWeight: 600,
                            mt: 0.25,
                            display: "block",
                        }}
                    >
                        {populationLabel}
                    </Typography>
                ) : null}
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        mt: 0.5,
                        fontWeight: 500,
                    }}
                >
                    Step {currentStepNumber} of {totalSteps}
                </Typography>
            </Stack>

            <Box
                sx={{
                    height: "1px",
                    bgcolor: "rgba(15, 23, 42, 0.12)",
                    mb: 2,
                }}
            />

            <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0, position: "relative" }}>
                {steps.map((step, idx) => {
                    const isDone = idx < currentIndex;
                    const isCurrent = idx === currentIndex;
                    const isLast = idx === steps.length - 1;
                    const inLoop = loopIndexes.includes(idx);

                    let dotBg = "#ffffff";
                    let dotBorder = "rgba(15, 23, 42, 0.18)";
                    let dotColor = "rgba(15, 23, 42, 0.45)";
                    let labelColor = "text.secondary";
                    let labelWeight = 500;

                    if (isDone) {
                        dotBg = "primary.main";
                        dotBorder = "primary.main";
                        dotColor = "primary.contrastText";
                    }
                    if (isCurrent) {
                        dotBg = "primary.main";
                        dotBorder = "primary.main";
                        dotColor = "primary.contrastText";
                        labelColor = "text.primary";
                        labelWeight = 700;
                    }

                    return (
                        <Box
                            key={step.key || idx}
                            component="li"
                            sx={{
                                position: "relative",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.25,
                                pb: isLast ? 0 : 2,
                            }}
                        >
                            {inLoop ? (
                                // One tinted band behind the repeated steps.
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        position: "absolute",
                                        left: -8,
                                        right: -8,
                                        top: idx === loopFirst ? -6 : 0,
                                        bottom: idx === loopLast ? 10 : 0,
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                        borderTopLeftRadius: idx === loopFirst ? 8 : 0,
                                        borderTopRightRadius: idx === loopFirst ? 8 : 0,
                                        borderBottomLeftRadius: idx === loopLast ? 8 : 0,
                                        borderBottomRightRadius: idx === loopLast ? 8 : 0,
                                    }}
                                />
                            ) : null}
                            {!isLast ? (
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        position: "absolute",
                                        left: 11,
                                        top: 24,
                                        bottom: -2,
                                        width: 2,
                                        bgcolor: isDone ? "primary.main" : "rgba(15, 23, 42, 0.10)",
                                        borderRadius: 1,
                                    }}
                                />
                            ) : null}

                            <Box
                                sx={{
                                    position: "relative",
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    bgcolor: dotBg,
                                    border: "2px solid",
                                    borderColor: dotBorder,
                                    color: dotColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                    boxShadow: isCurrent
                                        ? (theme) => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.18)}`
                                        : "none",
                                    transition: "box-shadow 120ms ease",
                                }}
                            >
                                {isDone ? <CheckIcon sx={{ fontSize: 14 }} /> : idx + 1}
                            </Box>

                            <Box sx={{ minWidth: 0, pt: "2px", position: "relative", flex: 1 }}>
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: labelColor,
                                            fontWeight: labelWeight,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                    {idx === loopFirst && loop?.label ? (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.25}
                                            sx={{
                                                flexShrink: 0,
                                                px: 0.75,
                                                py: "1px",
                                                borderRadius: 1,
                                                bgcolor: "primary.main",
                                                color: "primary.contrastText",
                                            }}
                                        >
                                            <LoopIcon sx={{ fontSize: 13 }} aria-hidden="true" />
                                            <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.4, whiteSpace: "nowrap" }}>
                                                {loop.label}
                                            </Typography>
                                        </Stack>
                                    ) : null}
                                </Stack>
                                {isCurrent ? (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: "block",
                                            mt: 0.25,
                                            color: "primary.main",
                                            fontWeight: 600,
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        IN PROGRESS
                                    </Typography>
                                ) : null}
                                {idx === loopLast && loop?.earlier?.length ? (
                                    <Stack spacing={0.25} sx={{ mt: 0.75 }} aria-label="Earlier iterations">
                                        {loop.earlier.map((line) => (
                                            <Typography
                                                key={line}
                                                variant="caption"
                                                sx={{ display: "block", color: "text.secondary", lineHeight: 1.35 }}
                                            >
                                                {line}
                                            </Typography>
                                        ))}
                                    </Stack>
                                ) : null}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
